<script setup lang="ts">
import { useUserStore } from '@store/user';
import { storeToRefs } from 'pinia';
import SectionAccount from '@sections/section-account.vue';
import PartNavigationList from '@parts/part-navigation-list.vue';
import PartUserId from '@parts/part-user-id.vue';
import PartSocialConnect from '@parts/part-social-connect.vue';
import PartInvitePromoGradientCompact from '@parts/part-invite-promo-gradient-compact.vue';
import PartReserveCard from '@parts/part-reserve-card.vue';
import { computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCommonStore } from '@/store/common';
import { useReserveStore } from '@store/reserve';

const commonStore = useCommonStore();
const reserveStore = useReserveStore();
const { t } = useI18n();
const userStore = useUserStore();
const { user } = storeToRefs(userStore);

// Резерв выдаётся не всем и живёт за настройкой бота, поэтому запрос
// best-effort: сам стор глотает ошибку, а карточка просто не рендерится.
onBeforeMount(() => {
	void reserveStore.load();
});

const fields = computed(() => ({
	title: t('accountIndex.title'),
	navigation: [
		// Подписка первой: резерв, оплата и история — то, ради чего заходят в кабинет
		{
			id: 'subscription',
			title: t('accountIndex.subscription'),
			list: [
				{
					title: t('accountIndex.subscribe'),
					url: "/payment",
					icon: {
						name: "wallet",
						viewBox: "0 0 18 18"
					}
				},
				{
					title: t('accountIndex.history'),
					url: "/account/history",
					icon: {
						name: "note-text",
						viewBox: "0 0 18 18"
					}
				},
			]
		},
		{
			id: 'settings',
			title: t('accountIndex.settings'),
			list: [
				{
					title: t('accountIndex.change_email'),
					url: "/account/email",
					icon: {
						name: "email",
						viewBox: "0 0 18 16"
					}
				},
				{
					title: t('accountIndex.change_language'),
					url: "/account/language",
					icon: {
						name: "globe",
						viewBox: "0 0 18 18"
					}
				},
			]
		},
		{
			id: 'documents',
			title: t('accountIndex.documents'),
			list: [
				{
					icon: {
						name : 'doc',
						viewBox: '0 0 18 18'
					},
					title: t('accountIndex.terms_of_service'),
					url_blank: commonStore.links.terms
				},
				{
					icon: {
						name : 'doc',
						viewBox: '0 0 18 18'
					},
					title: t('accountIndex.privacy_policy'),
          url_blank: commonStore.links.policy
				},
			]
		},
	],
	socials: {
		label: t('accountIndex.connect_label'),
	},
}));
</script>

<template>
	<main class="page page-profile">
		<transition
			appear
			name="show-up-container-items"
			:style="`--animation-delay: 0s;
				--animation-item-delay: 0.1s;
				--animation-duration: 0.6s;
				--animation-count: ${3}`"
		>
			<section-account
				:fields="fields"
			>
				<part-user-id
					:id="user?.id"
					class="page-profile__user"
				/>
				<part-social-connect
					v-bind="fields.socials"
					class="page-profile__social"
				/>
				<part-invite-promo-gradient-compact class="page-profile__invite" />
				<part-navigation-list
					v-for="nav in fields.navigation" :key="nav.id"
					:title="nav.title"
					:list="nav.list"
					class="page-profile__nav"
				>
					<!-- Карточка резерва — первый пункт «Подписки»; если резерва не было
						 или его не показываем, группа начинается с «Оплатить» -->
					<template
						v-if="nav.id === 'subscription' && reserveStore.isVisible"
						#prepend
					>
						<part-reserve-card />
					</template>
				</part-navigation-list>
			</section-account>
		</transition>
	</main>
</template>

<style lang="scss">
.page-profile {
	&__info {
		margin-bottom: 3.2rem;
	}
	&__nav {
		&:not(:last-child) {
			margin-bottom: 3.8rem;
			@include min-desktop {
				margin-bottom: 3.2rem;
			}
		}
	}
	&__user {
		margin-bottom: 0.8rem;
	}
	&__social {
		margin-bottom: 3.8rem;
		@include min-desktop {
			margin-bottom: 3.2rem;
		}
	}
	&__invite {
		margin-bottom: 3.8rem;
		@include min-desktop {
			margin-bottom: 3.2rem;
		}
	}
}
</style>
